// =============================================================================
// MODEL ALIAS — the served model id as DATA-IN-DB, keyed by a stable alias.
//
// The CONFIG pond for the inference layer's model selection (see
// docs/BUILD-PLAN-R72-measured-fix.md §3.3). One row = one alias's resolved model
// id, so a model upgrade is a one-row config flip, not a code change + redeploy.
// The pure consumer (ClaudeService.default()/fast()) never reads this table; it
// resolves through ModelAliasSource (KV-cached over CACHE_KV, falls back to the
// hardcoded ClaudeService constant floor, never throws) and the resolved string
// flows to the model param — the service stays config-agnostic.
//
// `most_current_anthropic` is the canonical key: it always points at the newest
// Anthropic Opus, so the next upgrade is one UPDATE (claude-opus-4-8 → 4-9). The
// 'fast'/'balanced' aliases mirror the Haiku/Sonnet tiers; a row need only exist
// to OVERRIDE the constant floor, so seeding just the Opus alias first is valid
// (the others fall to their constants = today's exact values, NO-DELETION).
//
// NO-INVENTION is untouched + model-agnostic: the grounding gate + the 3 linters
// run AFTER the model, so whichever model a row resolves, an invented value still
// cannot pass. Additive only (rule #11), applied via the Neon tool, mirrored here.
// =============================================================================

import { boolean, integer, pgTable, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

export const modelAlias = pgTable('model_alias', {
  id: id(),
  // The stable alias the code asks for, e.g. 'most_current_anthropic', 'fast',
  // 'balanced'. UNIQUE → one resolved model id per alias.
  alias: varchar('alias', { length: 48 }).notNull().unique(),
  // The concrete provider model id the alias resolves to, e.g. 'claude-opus-4-8'.
  // A model upgrade flips THIS value, never a .ts constant.
  modelId: varchar('model_id', { length: 96 }).notNull(),
  // Row toggle. An INACTIVE row is treated as absent by the Source (it never
  // silently applies), so the alias falls back to the constant floor.
  active: boolean('active').notNull().default(true),
  // Bumped by hand on each re-point; carried for audit / future cache-epoch invalidation.
  version: integer('version').notNull().default(1),
  ...timestamps,
});
